import {DebouncedContext} from "../../DebouncedContext";
import {WhitelabelLanguageDetector} from "../WhitelabelLanguageDetector";

const defaultApiEndpoint = '/api/geo';

export class AutocompleteApi {
    private endpoint: string

    constructor(endpoint: string | undefined = undefined) {
        const languagePrefix = new WhitelabelLanguageDetector().detectLanguage();
        this.endpoint = `${languagePrefix}${endpoint || defaultApiEndpoint}`;
    }

    private debounced = new DebouncedContext<AutocompleteResponse>(120)

    public async search(text: string, withProjects: boolean = true): Promise<AutocompleteResponse> {
        return await this.debounced.execute(this.fetch(text, withProjects))
    }

    private fetch(text: string, withProjects: boolean) {
        let projectsParam = (withProjects) ? "&projects=true" : "";
        const endpoint = this.endpoint;
        const self = this;
        return async function (abortController: AbortController): Promise<AutocompleteResponse> {
            function geoNameWithParent(geo: LegacyResult) {
                if (geo.parent_names && geo.parent_names.length > 0) {
                    return geo.name + " (" + geo.parent_names[0] + ")";
                }
                return geo.name;
            }

            try {
                let res = await fetch(
                    `${endpoint}?q=${encodeURIComponent(text)}${projectsParam}`,
                    {signal: abortController.signal}
                );
                let json = await self.getJson(res);
                if (json.results) {
                    return json as AutocompleteResponse
                }
                // Return with LegacyResult
                return new AutocompleteResponse([
                    new AutocompleteResult("",
                        (json as LegacyResult[])
                            .map(geo =>
                                    new AutocompletOption(geo.id, geoNameWithParent(geo), '')
                            )
                    )
                ]);
            } catch (exception) {
                return new AutocompleteResponse([])
            }
        }
    }

    private async getJson(res: Response) {
        return res.json();
    }
}

export class LegacyResult {
    id: string = ''
    name: string = ''
    url_name: string = ''
    parent_names: string[] = []

    constructor(id: string, name: string, url_name: string, parent_names: string[]) {
        this.id = id
        this.name = name
        this.url_name = url_name
        this.parent_names = parent_names
    }
}

export class AutocompleteResponse {
    results: AutocompleteResult[]

    constructor(results: AutocompleteResult[]) {
        this.results = results;
    }
}

export class AutocompleteResult {
    header: string = ""
    options: AutocompletOption[]

    constructor(header: string, options: AutocompletOption[]) {
        this.header = header;
        this.options = options;
    }
}

export class AutocompletOption {
    id: string
    text: string
    subtext: string

    constructor(id: string, text: string, subtext: string) {
        this.id = id;
        this.text = text;
        this.subtext = subtext;
    }
}